import { Injectable } from '@angular/core';
import { driver, type Config, type DriveStep } from 'driver.js';

export interface BillingDashboardTourContext {
  openManage: () => Promise<void>;
  closeManage: () => void;
  loadOverview: () => void;
}

const VIEW_TRANSITION_DELAY = 300;
const STEP_TRANSITION_DELAY = 150;

@Injectable({ providedIn: 'root' })
export class BillingDashboardTourService {
  start(context: BillingDashboardTourContext): void {
    // eslint-disable-next-line prefer-const -- assigned later because the steps array references it via closure
    let driverInstance: ReturnType<typeof driver>;

    const steps: DriveStep[] = [
      {
        element: '#tour-kpi-row',
        popover: {
          title: 'KPIs at a Glance',
          description:
            'The top row shows live KPI counts from both <strong>NetSapiens</strong> (hosted users, sites, domains) and <strong>Rev.io</strong> (active customers, products, MRR). These refresh when you navigate to the dashboard.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tour-overview-section',
        popover: {
          title: 'Billing Status Overview',
          description:
            'This table shows the billing health of every enabled domain. Each row compares what NS reports vs what Rev.io has billed. Click a row\'s arrow to jump directly into the Reconcile view for that domain.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '#tour-btn-load',
        popover: {
          title: 'Load / Refresh',
          description:
            'Click <strong>Load</strong> to compute the billing status for all enabled domains. Results are cached for 30 minutes — use <strong>Refresh</strong> to force a reload. The first run may take a few minutes.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '#tour-overview-table',
        popover: {
          title: 'Reading the Overview Table',
          description:
            'Each row represents one domain. The columns show:<br><br>' +
            '• <strong>Match</strong> — products with identical quantities in NS and Rev.io<br>' +
            '• <strong>Mismatch</strong> — quantities differ<br>' +
            '• <strong>Miss. Rev.io</strong> — product in NS but not billed in Rev.io<br>' +
            '• <strong>Extra Rev.io</strong> — billed in Rev.io but not in NS<br>' +
            '• <strong>Dup</strong> — duplicated service product entries<br>' +
            '• <strong>Static</strong> — fixed-price services exempt from NS comparison<br><br>' +
            'A highlighted row means there are issues to reconcile.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '#tour-btn-manage',
        popover: {
          title: 'Manage Domains',
          description:
            'Opens the domain management view where you can enable or disable domains for billing. Only enabled domains appear in the Overview table and are included in nightly batch reconciliation.',
          nextBtnText: 'Open Manage →',
          side: 'bottom',
          align: 'end',
          onNextClick: () => {
            context.openManage().then(() => {
              setTimeout(() => driverInstance.moveNext(), VIEW_TRANSITION_DELAY);
            });
          },
        },
      },
      {
        element: '#tour-manage-toolbar',
        popover: {
          title: 'Manage Domains — Bulk Actions',
          description:
            'Select domains with the checkboxes and use <strong>Enable Selected</strong> / <strong>Disable Selected</strong> to toggle multiple at once. Use the search bar to filter by domain name, customer, or description.',
          side: 'bottom',
          align: 'start',
          onPrevClick: () => {
            context.closeManage();
            setTimeout(() => driverInstance.movePrevious(), STEP_TRANSITION_DELAY);
          },
        },
      },
      {
        element: '#tour-manage-table',
        popover: {
          title: 'Manage Domains — Domain List',
          description:
            'Each row shows the Rev.io customer resolved for this domain, the user count, and whether a <strong>Manual</strong> mapping exists. The toggle activates or deactivates the domain for billing. Domains without a customer mapping cannot be enabled — use the link icon to set one up.',
          nextBtnText: 'Close & finish',
          side: 'top',
          align: 'start',
          onNextClick: () => {
            context.closeManage();
            setTimeout(() => driverInstance.moveNext(), STEP_TRANSITION_DELAY);
          },
        },
      },
    ];

    const config: Config = {
      showProgress: true,
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Done',
      progressText: '{{current}} of {{total}}',
      steps,
    };

    driverInstance = driver(config);
    driverInstance.drive();
  }
}
